
import { Link } from 'gatsby';
import React from "react"
import InnerLayout from './innerLayout';

type ProductId = 'markerjs' | 'mjslive' | 'cropro' | 'diagram';

interface IDocProduct {
  id: ProductId,
  name: string,
  firstDocSlug: string,
  referenceLink?: string
}

interface IDocsListItem {
  name: string,
  productId: ProductId,
  slug: string,
  children?: IDocsListItem[]
}

const docProducts: IDocProduct[] = [
  {
    id: 'markerjs',
    name: 'marker.js 2 Documentation',
    firstDocSlug: 'getting-started',
    referenceLink: '/reference'
  },
  {
    id: 'mjslive',
    name: 'marker.js Live Documentation',
    firstDocSlug: 'markerjs-live/getting-started',
    referenceLink: '/reference/markerjs-live'
  },
  {
    id: 'cropro',
    name: 'CROPRO Documentation',
    firstDocSlug: 'cropro/getting-started',
    referenceLink: '/reference/cropro'
  },
  {
    id: 'diagram',
    name: 'MJS Diagram Documentation',
    firstDocSlug: 'diagram/getting-started',
    referenceLink: '/reference/diagram'
  },
];

const markerjsDocList: IDocsListItem[] = [
  {
    name: 'Getting started',
    productId: 'markerjs',
    slug: 'getting-started',
    children: [
      {
        name: 'Using with React',
        productId: 'markerjs',
        slug: 'react-walkthrough'
      },
      {
        name: 'Using with Vue',
        productId: 'markerjs',
        slug: 'vue-walkthrough'
      }
    ]
  },
  {
    name: 'Configure available markers',
    productId: 'markerjs',
    slug: 'customizing-available-markers'
  },
  {
    name: 'Configuring defaults',
    productId: 'markerjs',
    slug: 'configuring-defaults'
  },
  {
    name: 'Events',
    productId: 'markerjs',
    slug: 'events'
  },
  {
    name: 'UI customization',
    productId: 'markerjs',
    slug: 'ui-customization'
  },
  {
    name: 'Display mode',
    productId: 'markerjs',
    slug: 'display-mode'
  },
  {
    name: 'Rendering options',
    productId: 'markerjs',
    slug: 'rendering-options'
  },
  {
    name: 'Saving & restoring state',
    productId: 'markerjs',
    slug: 'saving-state'
  },
  {
    name: 'Custom fonts',
    productId: 'markerjs',
    slug: 'custom-fonts'
  },
  {
    name: 'Creating custom markers',
    productId: 'markerjs',
    slug: 'extend-markerjs'
  },
];

const mjslDocList: IDocsListItem[] = [
  {
    name: 'Getting started',
    productId: 'mjslive',
    slug: 'markerjs-live/getting-started',
    children: [
      {
        name: 'Using with React',
        productId: 'mjslive',
        slug: 'markerjs-live/react-walkthrough'
      },
      {
        name: 'Using with Vue',
        productId: 'mjslive',
        slug: 'markerjs-live/vue-walkthrough'
      }
    ]
  },
  {
    name: 'Plugins',
    productId: 'mjslive',
    slug: 'markerjs-live/plugins',
  },
  {
    name: 'Events',
    productId: 'mjslive',
    slug: 'markerjs-live/events',
  },
  {
    name: 'Accessing elements from code',
    productId: 'mjslive',
    slug: 'markerjs-live/elements',
  },
  {
    name: 'Creating plugins',
    productId: 'mjslive',
    slug: 'markerjs-live/creating-plugins',
  },
];

const croproDocList: IDocsListItem[] = [
  {
    name: 'Getting started',
    productId: 'cropro',
    slug: 'cropro/getting-started',
    children: [
      {
        name: 'Using with React',
        productId: 'cropro',
        slug: 'cropro/react-walkthrough'
      },
      {
        name: 'Using with Vue',
        productId: 'cropro',
        slug: 'cropro/vue-walkthrough'
      }
    ]
  },
  {
    name: 'Configuring defaults',
    productId: 'cropro',
    slug: 'cropro/configure-defaults',
  },
  {
    name: 'Configuring aspect ratios',
    productId: 'cropro',
    slug: 'cropro/configure-aspect-ratios',
  },
  {
    name: 'UI customization',
    productId: 'cropro',
    slug: 'cropro/ui-customization',
  },
  {
    name: 'No or custom UI',
    productId: 'cropro',
    slug: 'cropro/no-ui',
  },
  {
    name: 'Rendering options',
    productId: 'cropro',
    slug: 'cropro/rendering-options',
  },
  // {
  //   name: 'More docs soon...',
  //   productId: 'cropro',
  //   slug: 'cropro/coming-soon',
  // }
];

const diagramDocList: IDocsListItem[] = [
  {
    name: 'Getting started',
    productId: 'diagram',
    slug: 'diagram/getting-started',
    children: [
      {
        name: 'Using with React',
        productId: 'diagram',
        slug: 'diagram/with-react',
      },
    ]
  },
  {
    name: 'Viewer vs. Editor',
    productId: 'diagram',
    slug: 'diagram/viewer-vs-editor',
  },
  {
    name: 'Stencilsets',
    productId: 'diagram',
    slug: 'diagram/stencilsets',
  },
  {
    name: 'Diagram settings',
    productId: 'diagram',
    slug: 'diagram/diagram-settings',
  },
  {
    name: 'Editor settings',
    productId: 'diagram',
    slug: 'diagram/editor-settings',
  },
  {
    name: 'Events',
    productId: 'diagram',
    slug: 'diagram/events',
  },
  {
    name: 'UI customization',
    productId: 'diagram',
    slug: 'diagram/customize-ui',
  },
  {
    name: 'More docs soon...',
    productId: 'diagram',
    slug: 'diagram/coming-soon',
  }
];

const DocsLayout = ({ title, current, children }: { title: string, current: string, children: React.ReactNode[] | React.ReactNode }) => {
  let docList: IDocsListItem[] = [];
  let currentProduct: IDocProduct | undefined;
  let docIndex = markerjsDocList.findIndex(doc => (
    doc.slug === current || (doc.children && doc.children.find(ch => ch.slug === current))
  ));
  if (docIndex > -1) {
    currentProduct = docProducts.find(p => p.id === 'markerjs');
    docList = markerjsDocList;
  } else {
    docIndex = croproDocList.findIndex(doc => (
      doc.slug === current || (doc.children && doc.children.find(ch => ch.slug === current))
    ));
    if (docIndex > -1) {
      currentProduct = docProducts.find(p => p.id === 'cropro');
      docList = croproDocList;
    } else {
      docIndex = mjslDocList.findIndex(doc => (
        doc.slug === current || (doc.children && doc.children.find(ch => ch.slug === current))
      ));
      if (docIndex > -1) {
        currentProduct = docProducts.find(p => p.id === 'mjslive');
        docList = mjslDocList;
      } else {
        docIndex = diagramDocList.findIndex(doc => (
          doc.slug === current || (doc.children && doc.children.find(ch => ch.slug === current))
        ));
        if (docIndex > -1) {
          currentProduct = docProducts.find(p => p.id === 'diagram');
          docList = diagramDocList;
        }
      }
    }
  }
  return (
    <InnerLayout title={title}>
      <div className="grid gap-6 grid-cols-12">
        <div className={docIndex > -1 ?
          "hidden md:block md:col-span-4 lg:col-span-3 bg-white rounded shadow-sm" :
          "block col-span-12 bg-white rounded shadow-sm"
        }>
          <ul>
            {docList.map(doc => {
              return (<li
                key={doc.slug}
                className={`${doc.slug === current ||
                  (doc.children !== undefined && doc.children.findIndex(c => c.slug === current) > -1) ?
                  'border-pink-400' : 'border-gray-300'} py-2 px-3 mb-px border-0 border-l-4 hover:border-pink-200`}
              ><Link to={`/docs/${doc.slug}`}
                className={`${doc.slug === current ? 'text-pink-600' : ''} hover:text-pink-400`}
              >{doc.name}</Link>
                {doc.children !== undefined && (<ul className="text-sm">
                  {doc.children.map(child => (<li
                    key={child.slug}
                    className={`py-1 px-3 mb-px hover:border-pink-200`}
                  ><Link to={`/docs/${child.slug}`}
                    className={`${child.slug === current ? 'text-pink-600' : ''} hover:text-pink-400`}
                  >{child.name}</Link>
                  </li>))
                  }
                </ul>)}
              </li>)
            })}
            {currentProduct && currentProduct.referenceLink && (<li
              className="py-2 px-3 mb-10 border-0 border-l-4 hover:border-pink-200"
            ><a href={currentProduct.referenceLink} target="_blank"
              className="hover:text-pink-400"
            >Class reference</a></li>
            )}
            {docProducts.map(product => {
              if (product.id !== currentProduct?.id) {
                return (
                  <li key={product.id}
                    className="py-2 px-3 mb-px border-0 border-l-4 border-yellow-200 hover:border-pink-200"
                  >
                    <Link to={`/docs/${product.firstDocSlug}`}
                      className="hover:text-pink-400 font-bold"
                    >{product.name}</Link>
                  </li>
                )
              }
            })}
          </ul>
        </div>
        <div className="col-span-12 md:col-span-8 lg:col-span-9">
          <section className="content">
            {children}
          </section>

          {docIndex > -1 && (
            <div className="flex flex-col place-items-center md:flex-row md:justify-between mt-8">
              {docIndex > 0 && (
                <Link
                  className="bg-white hover:bg-yellow-200 text-red-400 text-lg shadow hover:shadow-lg w-48 inline-block px-5 py-2 rounded-full text-center mb-3 md:mb-0"
                  to={`/docs/${docList[docIndex - 1].slug}`}>&laquo; Previous</Link>
              )}
              <span></span>
              {docIndex < docList.length - 1 && (
                <Link
                  className="bg-white hover:bg-yellow-200 text-red-400 text-lg shadow hover:shadow-lg w-48 inline-block px-5 py-2 rounded-full text-center"
                  to={`/docs/${docList[docIndex + 1].slug}`}>Next &raquo;</Link>
              )}
            </div>
          )}
        </div>
      </div>
    </InnerLayout>
  );
}

export default DocsLayout
